import React, { useRef, useState } from "react";
import html2canvas from "html2canvas";
import { motion } from "framer-motion";
import Avatar from "./Avatar";

function UserCardShareable({ userData, levelConfig, onClose }) {
  const cardRef = useRef(null);

  // Motive edici sıfatlar listesi
  const motivationalAdjectives = [
    "İlham Verici", "Azimli", "Yaratıcı", "Cesur", "Kararlı", 
    "Tutkulu", "Vizyoner", "Başarılı", "Özgüvenli", "Dinamik",
    "Motivasyon Dolu", "Hedef Odaklı", "İnatçı", "Enerjik", "Lider"
  ];

  const [adjectives] = useState(() => {
    const shuffled = [...motivationalAdjectives].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 3);
  });

  const handleShare = async () => {
    if (!cardRef.current) return;
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: null,
        scale: 2, // Yüksek çözünürlük
        useCORS: true
      });
      const image = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = image;
      link.download = `${userData?.username || 'yelken'}-id-card.png`;
      link.click();
      if (onClose) onClose();
    } catch (error) {
      console.error("Kart paylaşılırken hata oluştu:", error);
      alert("Bir hata oluştu, lütfen tekrar deneyin.");
    }
  };

  if (!userData) return null;

  return (
    <div style={containerStyle}>
      <div
        ref={cardRef}
        style={cardStyle}
      >
        {/* Holographic overlay */}
        <div style={hologramOverlayStyle}></div>
        
        {/* Header Strip */}
        <div style={headerStripStyle}>
           <span style={{ fontSize: '10px', letterSpacing: '2px', color: '#00ffcc', fontWeight: 'bold' }}>YELKEN_OS // ID_CARD</span>
           <span style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 'bold' }}>LVL.{userData.level || 1}</span>
        </div>

        {/* Profile Section */}
        <div style={profileSectionStyle}>
          <div style={avatarWrapperStyle}>
            <Avatar avatarData={userData.avatar} seedFallback={userData.username} size={80} />
            <div style={avatarGlowStyle}></div>
          </div>
          
          <div style={userInfoStyle}>
            <h3 style={usernameStyle}>@{userData.username || "Kullanıcı"}</h3>
            <p style={characterTypeStyle}>{userData.characterType || "Bilinmeyen Karakter"}</p>
          </div>
        </div>
        
        {/* Stats Grid */}
        <div style={statsGridStyle}>
           <div style={statBoxStyle}>
             <span style={statLabelStyle}>Hedef</span>
             <span style={statValueStyle}>{userData.target || "Belirtilmedi"}</span>
           </div>
           <div style={statBoxStyle}>
             <span style={statLabelStyle}>Alan</span>
             <span style={statValueStyle}>{userData.examType || "TYT-AYT"}</span>
           </div>
           <div style={statBoxStyle}>
             <span style={statLabelStyle}>Streak</span>
             <span style={statValueStyle}>{userData.currentStreak || 0} 🔥</span>
           </div>
           <div style={statBoxStyle}>
             <span style={statLabelStyle}>XP Durumu</span>
             <div style={{ width: '100%', height: '4px', background: 'rgba(255,255,255,0.1)', borderRadius: '2px', marginTop: '6px' }}>
                <div style={{ width: `${(userData.xp || 0) % 100}%`, height: '100%', background: '#00e676', borderRadius: '2px', boxShadow: '0 0 5px #00e676' }}></div>
             </div>
           </div>
        </div>

        {/* Adjectives */}
        <div style={adjectivesStyle}>
          {adjectives.map((adj, index) => (
            <span key={index} style={adjectiveBadgeStyle}>
              {adj}
            </span>
          ))}
        </div>
        
        {/* Footer/Watermark */}
        <div style={watermarkStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
             <div style={{ width: '12px', height: '12px', background: '#00ffcc', borderRadius: '50%' }}></div>
             <span style={{ fontSize: '12px', fontWeight: 'bold', letterSpacing: '1px' }}>EKSEN STUDIO</span>
          </div>
          <span style={{ fontWeight: 'bold', color: 'rgba(255,255,255,0.6)' }}>yelken.pro</span>
        </div>
      </div>
      
      <motion.button
        onClick={handleShare}
        style={shareButtonStyle}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        Kartı İndir & Paylaş
      </motion.button>
    </div>
  );
}

const containerStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: "100%",
};

const cardStyle = {
  background: "linear-gradient(145deg, #0f172a, #1e293b)",
  borderRadius: "16px",
  width: "100%",
  maxWidth: "340px",
  color: "#f8fafc",
  fontFamily: "'Inter', sans-serif",
  boxShadow: "0 20px 40px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255,255,255,0.1)",
  border: "1px solid rgba(99, 102, 241, 0.3)",
  position: "relative",
  overflow: "hidden",
};

const hologramOverlayStyle = {
  position: "absolute",
  top: 0, left: 0, right: 0, bottom: 0,
  background: "linear-gradient(125deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0) 40%, rgba(0, 255, 204, 0.05) 100%)",
  pointerEvents: "none",
  zIndex: 1,
};

const headerStripStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "12px 20px",
  borderBottom: "1px solid rgba(255,255,255,0.05)",
  background: "rgba(0,0,0,0.2)",
  position: "relative",
  zIndex: 2,
};

const profileSectionStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "24px 20px 16px",
  position: "relative",
  zIndex: 2,
};

const avatarWrapperStyle = {
  position: "relative",
  marginBottom: "16px",
  border: "2px solid rgba(0, 255, 204, 0.5)",
  borderRadius: "50%",
  padding: "4px",
  background: "rgba(0, 0, 0, 0.2)",
};

const avatarGlowStyle = {
  position: "absolute",
  top: "50%", left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90px", height: "90px",
  borderRadius: "50%",
  background: "rgba(0, 255, 204, 0.2)",
  filter: "blur(15px)",
  zIndex: -1,
};

const userInfoStyle = {
  textAlign: "center",
};

const usernameStyle = {
  fontSize: "22px",
  fontWeight: "800",
  color: "#f8fafc",
  marginBottom: "4px",
  letterSpacing: "-0.5px",
};

const characterTypeStyle = {
  fontSize: "14px",
  color: "#00ffcc",
  fontWeight: "600",
  textTransform: "uppercase",
  letterSpacing: "1px",
};

const statsGridStyle = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "10px",
  padding: "0 20px 20px",
  position: "relative",
  zIndex: 2,
};

const statBoxStyle = {
  background: "rgba(0,0,0,0.2)",
  border: "1px solid rgba(255,255,255,0.05)",
  borderRadius: "8px",
  padding: "10px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
};

const statLabelStyle = {
  fontSize: "10px",
  color: "#94a3b8",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  marginBottom: "4px",
};

const statValueStyle = {
  fontSize: "13px",
  fontWeight: "bold",
  color: "#e2e8f0",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const adjectivesStyle = {
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "center",
  gap: "8px",
  padding: "0 20px 20px",
  position: "relative",
  zIndex: 2,
};

const adjectiveBadgeStyle = {
  background: "rgba(99, 102, 241, 0.15)",
  border: "1px solid rgba(99, 102, 241, 0.3)",
  color: "#818cf8",
  padding: "6px 12px",
  borderRadius: "20px",
  fontSize: "11px",
  fontWeight: "bold",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

const watermarkStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "12px 20px",
  background: "rgba(0,0,0,0.3)",
  borderTop: "1px solid rgba(255,255,255,0.05)",
  fontSize: "12px",
  color: "rgba(255,255,255,0.4)",
  position: "relative",
  zIndex: 2,
};

const shareButtonStyle = {
  background: "linear-gradient(90deg, #6366f1, #8b5cf6)",
  border: "none",
  padding: "14px 24px",
  borderRadius: "12px",
  color: "#fff",
  fontSize: "15px",
  fontWeight: "bold",
  cursor: "pointer",
  marginTop: "20px",
  width: "100%",
  boxShadow: "0 8px 16px rgba(99, 102, 241, 0.3)",
};

export default UserCardShareable;